import type { SubscriptionType } from '../../types/subscription';
import type { NegotiatedOffer } from '../../types/rest-api';

/** sessionStorage key — plan chosen on `/onboarding/municipality/plan` before profile steps. */
export const MUNICIPALITY_PLAN_STORAGE_KEY =
  'bn_municipality_plan_selection_v1';

/**
 * Persists Schulgemeinde plan choices until onboarding completes.
 * **Order placement** (`POST /v1/licenses/orders`) happens at checkout after the profile wizard,
 * not when leaving the plan step. Route guards ensure plan + (for paid) license catalog exist
 * before `/onboarding/municipality` loads.
 */
export interface MunicipalityPlanSelection {
  subscriptionType: SubscriptionType;
  /** Billing commitment (discount tier). */
  durationYears: 1 | 2 | 3;
  extraAdmins: number;
  totalSchoolUnits: number;
  monthlyTotalChf: number;
  annualTotalChf: number;
  discountPercent: number;
  selectedAt: string;
}

export function saveMunicipalityPlanSelection(
  data: Omit<MunicipalityPlanSelection, 'selectedAt'> & { selectedAt?: string }
): void {
  const payload: MunicipalityPlanSelection = {
    ...data,
    selectedAt: data.selectedAt ?? new Date().toISOString(),
  };
  sessionStorage.setItem(
    MUNICIPALITY_PLAN_STORAGE_KEY,
    JSON.stringify(payload)
  );
}

export function loadMunicipalityPlanSelection(): MunicipalityPlanSelection | null {
  try {
    const raw = sessionStorage.getItem(MUNICIPALITY_PLAN_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as MunicipalityPlanSelection;
  } catch {
    return null;
  }
}

export function clearMunicipalityPlanSelection(): void {
  sessionStorage.removeItem(MUNICIPALITY_PLAN_STORAGE_KEY);
}

/** sessionStorage key — negotiated offer accepted on the plan step, consumed at checkout. */
export const NEGOTIATED_OFFER_STORAGE_KEY = 'bn.onboarding.negotiatedOffer';

export function saveNegotiatedOfferSelection(offer: NegotiatedOffer): void {
  sessionStorage.setItem(NEGOTIATED_OFFER_STORAGE_KEY, JSON.stringify(offer));
}

export function loadNegotiatedOfferSelection(): NegotiatedOffer | null {
  try {
    const raw = sessionStorage.getItem(NEGOTIATED_OFFER_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as NegotiatedOffer;
  } catch {
    return null;
  }
}

export function clearNegotiatedOfferSelection(): void {
  sessionStorage.removeItem(NEGOTIATED_OFFER_STORAGE_KEY);
}
